import { useMemo } from 'react'
import { ITDiscountDisplay, ITTypeQuantityDisplay } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { InputTableLayoutStepProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { getAllSpecificPriceRules, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import BuyOrderCreateNoPermissionStepITHeader from './BuyOrderCreateNoPermissionStepITHeader'
import { CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../../utils/globals/dictionary'
import OrderBuyLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout'
import useOrderRowRenderer from '../../../../../../../../../utils/hooks/orders/useOrderRowRenderer'
import { BuyOrderStatusInversed } from '@teinor/erp/types/company/order/buy/buyOrder'
import { BuyOrderProductModelTypeExtended } from '@teinor/erp/types/company/order/buy/buyOrder/buyOrderProduct'
import useBuyOrderHeaderURLs from '../../../../../../../../../utils/hooks/buys/orders/useBuyOrderHeaderURLsN'
import BuyOrderCreateNoPermissionStepHeaderDecider from './BuyOrderCreateNoPermissionHeader'
import { CustomRenderPriceWithMargin, CustomRenderSpecificPrice } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/CustomRenders'

interface BuyOrderCreateNoPermissionStepProps extends InputTableLayoutStepProps {}

const BuyOrderCreateNoPermissionStep: React.FC<BuyOrderCreateNoPermissionStepProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const { rowRenderer } = useBuyOrderCreateNoPermissionStepLogic({ completeData })
    const accounts = useMemo(() => dictionaryComplexFromJsonArr(completeData?.header?.OrderAccounts), [completeData?.header?.OrderAccounts])
    const orderHeaderURLs = useBuyOrderHeaderURLs()
    const specificPriceRules = getAllSpecificPriceRules()
    if (!completeData) {
        return null
    }
    const priceColumns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & BuyOrderProductModelTypeExtended> = []
    const priceColumns2: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & BuyOrderProductModelTypeExtended> = []
    const hidePrices = completeData?.header?.hidePrices
    if(!hidePrices){
        priceColumns.push(
            {
                id: 'initialUnitPriceWithoutTax',
                dataKey: 'initialUnitPriceWithoutTax',
                label: () => <div className='text-right mr-15 w-full'>{t('cost', 'Coste')}</div>,
                simpleLabel: t('cost', 'Coste'),
                component: 'ITDisplayPrice',
                width: 100,
            },
            {
                id: 'Product.SpecificPriceRuleId',
                dataKey: 'Product.SpecificPriceRuleId',
                label: (props) => {
                    const myValue = parseInt(props.selectedFilterValue || props.value || specificPriceRules?.defaultSpecificPriceRuleId)
                    return <span>{t('pvp', 'PVP') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
                },
                simpleLabel: t('sellPrice', 'Precio de venta'),
                searchableComponent: {
                    component: 'SearchableComponentsCustomSelectSearchBar',
                    extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
                },
                customRender: (parameterValue, allRowData) => <CustomRenderSpecificPrice specificPriceRules={specificPriceRules} allRowData={allRowData} />,
            },
            {
                id: 'Product.SpecificPriceRuleIdActual',
                dataKey: 'Product.SpecificPriceRuleId',
                label: t('costPriceWithMarginShort', 'PCM'),
                simpleLabel: t('costPriceWithMargin', 'Precio coste con margen'),
                toolTip: t('costPriceWithMargin', 'Precio coste con margen'),
                customRender: (parameterValue, allRowData) => <CustomRenderPriceWithMargin allRowData={allRowData} CurrencyId={completeData?.header?.CurrencyId} />,
            },
            {
                id: 'margin',
                dataKey: 'margin',
                label: t('margin', 'Margen'),
                customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
                hideColumnByDefault: true,
                width: 100,
            },
        )
        priceColumns2.push(
            {
                id: 'OrderAccountId',
                dataKey: 'OrderAccountId',
                label: t('chapter', 'Capítulo'),
                customRender: (parameterValue) => <CustomRenderString value={accounts[parameterValue]?.name || ''} />,
                hideColumnByDefault: true,
            },
            {
                id: 'discount',
                dataKey: 'discount',
                label: t('discount', 'Descuento'),
                width: 90,
                customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} />
            },
            {
                id: 'rowPriceWithoutTaxPrev',
                dataKey: 'rowPriceWithoutTaxPrev',
                label: () => <div className='text-right mr-15 w-full'>{t('rowCost', 'Coste Línea')}</div>,
                simpleLabel: t('rowCost', 'Coste Línea'),
                component: 'ITDisplayFinalPrice',
                with_vat: false,
                width: 120,
                hideColumnByDefault: true
            },
            {
                id: 'rowPriceWithoutTaxAndTotalDiscount',
                dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
                label: () => <div className='text-right w-full'>{t('costTotal', 'Coste Total')}</div>,
                simpleLabel: t('costTotal', 'Coste Total'),
                component: 'ITDisplayFinalPrice',
                with_vat: false,
                width: 120,
            },
            {
                id: 'rowPriceWithTaxAndTotalDiscount',
                dataKey: 'rowPriceWithTaxAndTotalDiscount',
                label: () => <div className='text-right w-full'>{t('totalCostWithTaxShort', 'Coste Total Imp.')}</div>,
                simpleLabel: t('totalCostWithTaxShort', 'Coste Total Imp.'),
                component: 'ITDisplayFinalPrice',
                with_vat: false,
                width: 130,
                hideColumnByDefault: true,
            },
            {
                id: 'totalWithTaxWithComplements',
                dataKey: 'totalWithTaxWithComplements',
                simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
                label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
                component: 'ITDisplayPrice',
                width: 160,
                hideColumnByDefault: true,
                toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
            },
            {
                id: 'totalWithoutTaxWithComplements',
                dataKey: 'totalWithoutTaxWithComplements',
                simpleLabel: t('totalWithComplements', 'Total con complementos'),
                label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
                component: 'ITDisplayPrice',
                width: 110,
                hideColumnByDefault: true,
                toolTip: t('totalWithComplements', 'Total con complementos')
            },
            {
                id: 'totalWithoutTaxWithComplementsPrev',
                dataKey: 'totalWithoutTaxWithComplementsPrev',
                simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
                label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
                component: 'ITDisplayPrice',
                width: 110,
                hideColumnByDefault: true,
                toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
            }, 
            {
                id: 'unitPriceWithComplements',
                dataKey: 'unitPriceWithComplements',
                simpleLabel: t('unitCostWithComplements', 'Coste unitario con complementos'),
                label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
                component: 'ITDisplayPrice',
                width: 110,
                hideColumnByDefault: true,
                toolTip: t('unitCostWithComplements', 'Coste unitario con complementos'),
            }, 
        )
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & BuyOrderProductModelTypeExtended> = [
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchSupplierProductInputReference',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            SupplierId: completeData?.header?.SupplierId,
            hideColumnByDefault: true
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            sortable: true,
            minWidth: 180
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />
        },
        {
            id: 'supplier_reference',
            dataKey: 'supplier_reference',
            label: t('supplierRefShort', 'Ref. Prov.'),
            simpleLabel: t('supplierRefLong', 'Referencia Proveedor'),
            toolTip: t('supplierRefLong', 'Referencia Proveedor'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        ...priceColumns,
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            width: 110,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            width: 110,
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            customRender: (parameterValue) => <CustomRenderString value={completeData?.header?.Warehouse?.name || ''} />,
            hideColumnByDefault: true,
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
        ...priceColumns2
    ]
    return (
        <OrderBuyLayout modelId='BuyOrderId' name={'BuyOrderCreateNoPermissionStep' + (hidePrices ? 'hidePrices' : '')} translations={{ headerTitle: t('buyOrder', 'Pedido') }} hideNotes={true} columns={columns}
         buttonSave={
             <BuyOrderCreateNoPermissionStepHeaderDecider orderStatus={completeData?.header.status} isReadOnly={true} OrderLogs={completeData?.header?.OrderLogs} onSubmit={() => { }} data={completeData as OrderData} />
        } InputTableHeaderChildren={BuyOrderCreateNoPermissionStepITHeader} isReadOnly={true} {...{ rowRenderer, inputTableURLs, orderHeaderURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, headerChanges, setTableConfiguration, userPermissions,  orderStatusDict: BuyOrderStatusInversed }} />
    )
}

interface BuyOrderCreateNoPermissionStepLogicProps {
    completeData: OrderData | null
}

export const useBuyOrderCreateNoPermissionStepLogic = ({ completeData }: BuyOrderCreateNoPermissionStepLogicProps) => {
    const rowRenderer = useOrderRowRenderer({ completeData, isFinishedStep: true })

    return { rowRenderer }
}

export default BuyOrderCreateNoPermissionStep